import React, { useState, useEffect } from 'react';
import { FaMoneyBillWave, FaCreditCard, FaPaypal, FaCcStripe, FaCcVisa, FaUserAlt, FaHome, FaStore } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { API_CONFIG, API_ENDPOINTS } from '../config/apiConfig';
import { 
  getPaymentMethods, 
  getUserAddresses, 
  createOrder,
  sendNotificationToStores,
  sendOrderEmail 
} from '../api/authApi';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const { user, token } = useAuth();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: delivery, 2: address, 3: payment
  
  // Delivery options
  const [deliveryOption, setDeliveryOption] = useState('home'); // 'home' or 'store'
  
  // Addresses
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  
  // Store addresses
  const [storeAddresses, setStoreAddresses] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  
  // Payments
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [payMethodName, setPayMethodName] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Order processing
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Coupons/Offers
  const [offers, setOffers] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [loadingOffers, setLoadingOffers] = useState(false);
  
  // Wallet
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [loadingWallet, setLoadingWallet] = useState(false);
  
  // Order summary toggle for mobile
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  
  // Form data for new address
  const [formData, setFormData] = useState({
    address: '',
    house: '',
    landmark: '',
    title: 'home',
    pincode: '',
    lat: '',
    lng: ''
  });
  
  // User info from auth context
  const userInfo = {
    id: user?.id || localStorage.getItem('uid') || null,
    email: user?.email || localStorage.getItem('userEmail') || 'guest@example.com',
    first_name: user?.first_name || localStorage.getItem('userFirstName') || 'Guest',
    last_name: user?.last_name || localStorage.getItem('userLastName') || 'User',
    mobile: user?.mobile || localStorage.getItem('userMobile') || '',
  };

  // Check if user is logged in
  const isLoggedIn = userInfo.id && userInfo.id !== 'null' && token;
  
  // Calculate totals
  const subtotal = cart.totalPrice || 0;
  const discount = cart.discount || 0;
  const deliveryCharge = cart.deliveryPrice || 0;
  const tax = cart.orderTax ? (subtotal * cart.orderTax / 100) : 0;
  const total = cart.grandTotal || 0;
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission for new address
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Add address creation logic here
    console.log('Create new address:', formData);
  };
  
  // Helper function to get item image
  const getItemImage = (item) => {
    if (item.cover && typeof item.cover === 'string' && item.cover.trim()) {
      return item.cover.startsWith('http') ? item.cover : `${API_CONFIG.IMAGE_URL}${item.cover}`;
    }
    return '/assets/img/product/default.png';
  };
  
  // Helper function to get item price
  const parsePrice = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const str = String(value).trim();
    if (str === '') return 0;
    const cleaned = str.replace(/[^0-9.-]+/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };
  
  const getItemPrice = (item) => {
    const discountValue = parseFloat(item.discount) || 0;
    if (item && discountValue === 0) {
      if ((item.size === '1' || item.size === 1) && item.variations && item.variations.length > 0) {
        const variationGroup = item.variations[0];
        const selectedVariation = variationGroup?.items?.[item.variant || 0];
        if (selectedVariation) {
          const discountVal = parsePrice(selectedVariation.discount);
          const priceVal = parsePrice(selectedVariation.price);
          return discountVal && discountVal !== 0 ? discountVal : priceVal;
        }
      }
      return parsePrice(item.original_price);
    } else {
      if ((item.size === '1' || item.size === 1) && item.variations && item.variations.length > 0) {
        const variationGroup = item.variations[0];
        const selectedVariation = variationGroup?.items?.[item.variant || 0];
        if (selectedVariation) {
          const discountVal = parsePrice(selectedVariation.discount);
          const priceVal = parsePrice(selectedVariation.price);
          return discountVal && discountVal !== 0 ? discountVal : priceVal;
        }
      }
      return parsePrice(item.sell_price);
    }
  };
  
  const getItemTotal = (item) => {
    return (getItemPrice(item) * item.quantiy).toFixed(2);
  };
  
  // Load payment methods when reaching step 3
  useEffect(() => {
    if (isLoggedIn && paymentMethods.length === 0) {
      loadPaymentMethods();
    }
  }, [isLoggedIn]);
  
  // Load addresses when component mounts (if logged in)
  useEffect(() => {
    if (isLoggedIn && addresses.length === 0) {
      loadAddresses();
    }
  }, [isLoggedIn]);
  
  const loadAddresses = async () => {
    if (!token || !userInfo.id) return;
    setLoadingAddresses(true);
    try {
      const result = await getUserAddresses(userInfo.id, token);
      if (result.success && result.data) {
        setAddresses(result.data);
        // Auto-select first address
        if (result.data.length > 0 && !selectedAddressId) {
          setSelectedAddressId(result.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load addresses:', error);
    } finally {
      setLoadingAddresses(false);
    }
  };
  
  const loadPaymentMethods = async () => {
    if (!token) return;
    setLoadingPayments(true);
    try {
      const result = await getPaymentMethods(token);
      console.log('Payment methods API result:', result);
      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        setPaymentMethods(result.data);
        // Auto-select COD if available
        const codMethod = result.data.find(p => p.id === 1);
        if (codMethod) {
          setSelectedPaymentId(1);
          setPayMethodName(codMethod.name);
        }
      } else {
        showMessage('error', result.message || 'No payment methods found');
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      showMessage('error', 'Failed to load payment methods');
      setPaymentMethods([]);
    } finally {
      setLoadingPayments(false);
    }
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  
  const handlePaymentSelection = (method) => {
    console.log('Payment method selected:', method);
    setSelectedPaymentId(method.id);
    setPayMethodName(method.name);
    setCurrencyCode(method.currency_code || 'USD');
  };
  
  const handleProceedToPay = async () => {
    if (!selectedPaymentId) {
      showMessage('error', 'Please select a payment method');
      return;
    }
    
    if (cart.cart.length === 0) {
      showMessage('error', 'Your cart is empty');
      return;
    }
    
    if (cart.totalPrice < cart.minOrderPrice) {
      showMessage('error', `Minimum order amount must be $${cart.minOrderPrice} or more`);
      return;
    }
    
    // Route to payment handler
    if (selectedPaymentId === 1) {
      await handleCODPayment();
    } else {
      showMessage('info', `${payMethodName} payment coming soon`);
    }
  };
  
  const handleCODPayment = async () => {
    setProcessing(true);
    try {
      const storeIds = cart.getStoreIds();
      const orderStatusByStore = storeIds.map(id => ({ id, status: 'created' }));
      
      const orderNotes = [{
        status: 1,
        value: 'Order Created',
        time: moment().format('lll')
      }];
      
      const orderData = {
        uid: userInfo.id,
        store_id: storeIds.join(','),
        date_time: moment().format('YYYY-MM-DD HH:mm:ss'),
        paid_method: payMethodName,
        order_to: deliveryOption,
        orders: JSON.stringify(cart.cart),
        notes: JSON.stringify(orderNotes),
        address: deliveryOption === 'home' && addresses.length > 0 
          ? JSON.stringify(addresses.find(a => a.id === selectedAddressId)) 
          : '',
        driver_id: '',
        total: cart.totalPrice,
        tax: cart.orderTax ? (cart.totalPrice * cart.orderTax / 100) : 0,
        grand_total: cart.grandTotal,
        delivery_charge: cart.deliveryPrice,
        coupon_code: cart.coupon ? JSON.stringify(cart.coupon) : '',
        discount: cart.discount,
        pay_key: 'cod',
        status: JSON.stringify(orderStatusByStore),
        assignee: '',
        extra: JSON.stringify(cart.userOrderTaxByStores || []),
        payStatus: 1,
        wallet_used: cart.walletDiscount > 0 ? 1 : 0,
        wallet_price: cart.walletDiscount
      };
      
      const result = await createOrder(orderData, token);
      
      if (result.success && result.data?.id) {
        const newOrderId = result.data.id;
        
        cart.clearCart();
        
        try {
          await sendNotificationToStores(storeIds, newOrderId, token);
          await sendOrderEmail({
            id: newOrderId,
            mediaURL: API_CONFIG.IMAGE_URL,
            subject: 'New Order Created',
            email: userInfo.email,
            username: `${userInfo.first_name} ${userInfo.last_name}`
          }, token);
        } catch (notifError) {
          console.error('Notification/Email error:', notifError);
        }
        
        showMessage('success', `Order #${newOrderId} created successfully!`);
        
        setTimeout(() => {
          navigate('/my-account');
        }, 2000);
      } else {
        showMessage('error', result.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('COD payment error:', error);
      showMessage('error', 'Failed to process order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Header />
      <main className="main__content_wrapper" style={{background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)', minHeight: '60vh'}}>
        <div className="breadcrumb__section" style={{background: 'url(/assets/img/banner/breadcrumb-bg.jpg) center/cover no-repeat', padding: '48px 0 32px 0'}}>
          <div className="container">
            <h1 className="breadcrumb__title text-center" style={{color: '#fff', fontSize: '2.5rem', fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.15)'}}>Checkout</h1>
          </div>
        </div>
        {/* Stepper */}
        <div className="container mt-4 mb-2">
          <div className="checkout-stepper d-flex justify-content-center align-items-center gap-4" style={{maxWidth: 600, margin: '0 auto'}}>
            {["Information", "Shipping", "Payment"].map((step, idx) => (
              <div key={step} className={`stepper-step d-flex flex-column align-items-center ${currentStep === idx+1 ? 'active' : ''}`} style={{flex: 1}}>
                <div className={`stepper-circle mb-1 ${currentStep >= idx+1 ? 'bg-primary text-white' : 'bg-light text-muted'}`} style={{width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18}}>{idx+1}</div>
                <span style={{fontSize: 14, fontWeight: currentStep === idx+1 ? 600 : 400}}>{step}</span>
              </div>
              ))}
          </div>
        </div>
        {/* Message Display */}
        {message.text && (
          <div className="container mt-3">
            <div className={`alert alert-${message.type === 'success' ? 'success' : message.type === 'info' ? 'info' : 'danger'} alert-dismissible fade show`} role="alert">
              {message.text}
              <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
            </div>
          </div>
        )}
        <section className="checkout__page--area">
          <div className="container py-5">
            <div className="checkout__page--inner d-flex flex-wrap gap-4 justify-content-center">
              <div className="main checkout__mian card shadow-lg rounded-4 border-0 p-4" style={{minWidth: '340px', maxWidth: '700px', flex: '1 1 700px', background: '#fff'}}>
                <header className="main__header checkout__mian--header mb-30">
                  <a className="logo logo__left mb-20" href="/">
                    <img src="/assets/img/logo/nav-log.png" alt="logo" />
                  </a>
                </header>
                <main className="main__content_wrapper section--padding pt-0">
                  <form onSubmit={handleSubmit}>
                    {/* Delivery Options Section */}
                    <div className="checkout__content--step section__shipping--address mb-4">
                      <div className="section__header mb-25">
                        <h2 className="section__header--title h3">Delivery Options</h2>
                        <p className="section__header--desc">Choose your preferred delivery method.</p>
                      </div>
                      <div className="checkout__content--step__inner3 border-radius-5 p-3" style={{background: '#f6f8fa'}}>
                        {/* Delivery Type Selection with icons */}
                        <div className="mb-20 d-flex gap-4">
                          <div className="shipping__contact--box__list flex-grow-1">
                            <div className="shipping__radio--input d-flex align-items-center gap-2">
                              <input
                                className="shipping__radio--input__field"
                                id="delivery-home"
                                name="deliveryType"
                                type="radio"
                                value="home"
                                checked={deliveryOption === 'home'}
                                onChange={(e) => setDeliveryOption(e.target.value)}
                              />
                              <span style={{fontSize: 22}} aria-label="Home"><FaHome color="#27ae60" /></span>
                              <label className="shipping__radio--label ms-2" htmlFor="delivery-home">
                                <span className="shipping__radio--label__primary">Home Delivery</span>
                                <span className="shipping__radio--label__desc">Deliver to your address</span>
                              </label>
                            </div>
                          </div>
                          <div className="shipping__contact--box__list flex-grow-1">
                            <div className="shipping__radio--input d-flex align-items-center gap-2">
                              <input
                                className="shipping__radio--input__field"
                                id="delivery-store"
                                name="deliveryType"
                                type="radio"
                                value="store"
                                checked={deliveryOption === 'store'}
                                onChange={(e) => setDeliveryOption(e.target.value)}
                              />
                              <span style={{fontSize: 22}} aria-label="Store"><FaStore color="#2980b9" /></span>
                              <label className="shipping__radio--label ms-2" htmlFor="delivery-store">
                                <span className="shipping__radio--label__primary">Store Pickup</span>
                                <span className="shipping__radio--label__desc">Collect from store</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Address Selection - Show only for Home Delivery */}
                    {(deliveryOption === 'home') && (
                      <div className="checkout__content--step section__shipping--address mb-4">
                        <div className="section__header mb-25">
                          <h2 className="section__header--title h3 d-flex align-items-center gap-2">
                            <FaHome color="#27ae60" style={{fontSize: 24}} />
                            Delivery Address
                          </h2>
                          <p className="section__header--desc">Select the address where you want your order delivered.</p>
                        </div>
                        {loadingAddresses ? (
                          <div className="checkout__content--step__inner3 border-radius-5 p-4 text-center">
                            <p>Loading addresses...</p>
                          </div>
                        ) : addresses.length > 0 ? (
                          <div className="checkout__content--step__inner3 border-radius-5 p-3" style={{background: '#f6f8fa'}}>
                            {addresses.map((addr) => (
                              <div key={addr.id} className="shipping__contact--box__list d-flex align-items-center gap-2 mb-2">
                                <input
                                  className="shipping__radio--input__field"
                                  id={`address-${addr.id}`}
                                  name="deliveryAddress"
                                  type="radio"
                                  checked={selectedAddressId === addr.id}
                                  onChange={() => setSelectedAddressId(addr.id)}
                                />
                                <span style={{fontSize: 20}} aria-label="Address"><FaHome color="#27ae60" /></span>
                                <label className="shipping__radio--label ms-2" htmlFor={`address-${addr.id}`}>
                                  <span className="shipping__radio--label__primary">{addr.title}</span>
                                  <span className="shipping__radio--label__desc">
                                    {addr.house && `${addr.house}, `}
                                    {addr.landmark && `${addr.landmark}, `}
                                    {addr.address}
                                    {addr.pincode && ` - ${addr.pincode}`}
                                  </span>
                                </label>
                              </div>
                            ))}
                            <div className="checkout__content--step__footer mt-3">
                              <Link to="/my-account" className="checkout__continue__btn btn">
                                Add New Address
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <div className="checkout__content--step__inner3 border-radius-5 p-4">
                            <p className="text-danger mb-3">No addresses found. Please add a delivery address.</p>
                            <Link to="/my-account" className="checkout__continue__btn btn">
                              Add Address
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Contact Review Only */}
                    {/* Contact Review - show for both Home and Store */}
                    <div className="checkout__content--step checkout__contact--information2 border-radius-5 mb-4 p-3" style={{background: '#f6f8fa'}}>
                      <div className="checkout__review d-flex justify-content-between align-items-center mb-2">
                        <div className="checkout__review--inner d-flex align-items-center gap-2">
                          <span style={{fontSize: 20}} aria-label="Contact"><FaUserAlt color="#6366f1" /></span>
                          <label className="checkout__review--label">Contact</label>
                          <span className="checkout__review--content ms-2">{userInfo.mobile ? userInfo.mobile : userInfo.email}</span>
                        </div>
                        <div className="checkout__review--link">
                          <button className="checkout__review--link__text" type="button" onClick={() => navigate('/my-account?editProfile=true')}>Change</button>
                        </div>
                      </div>
                    </div>
                    {/* Payment Section */}
                    <div className="checkout__content--step section__shipping--address mb-4">
                      <div className="section__header mb-25">
                        <h2 className="section__header--title h3">Payment</h2>
                        <p className="section__header--desc">All transactions are secure and encrypted.</p>
                      </div>
                      {/* Payment Methods List with icons */}
                      {loadingPayments ? (
                        <div className="checkout__content--step__inner3 border-radius-5 p-4 text-center">
                          <p>Loading payment methods...</p>
                        </div>
                      ) : paymentMethods.length > 0 ? (
                        <div className="checkout__content--step__inner3 border-radius-5 p-3" style={{background: '#f6f8fa'}}>
                          {paymentMethods.map((method) => (
                            <div key={method.id} className="shipping__contact--box__list d-flex align-items-center gap-2 mb-2">
                              <input
                                className="shipping__radio--input__field"
                                id={`payment-${method.id}`}
                                name="paymentMethod"
                                type="radio"
                                checked={selectedPaymentId === method.id}
                                onChange={() => handlePaymentSelection(method)}
                              />
                              <span style={{fontSize: 22}} aria-label="Payment">
                                {(() => {
                                  switch (method.id) {
                                    case 1: return <FaMoneyBillWave color="#27ae60" />; // COD
                                    case 2: return <FaCcStripe color="#635bff" />; // Stripe
                                    case 3: return <FaPaypal color="#003087" />; // PayPal
                                    case 4: return <FaCcVisa color="#1a1f71" />; // PayTM (Visa icon as alternative)
                                    case 5: return <FaCreditCard color="#0f4fa8" />; // RazorPay (generic card)
                                    case 6: return <FaCreditCard color="#2d3436" />; // InstaMOJO
                                    case 7: return <FaCreditCard color="#2d3436" />; // PayStack
                                    case 8: return <FaCreditCard color="#2d3436" />; // Flutterwave
                                    default: return <FaCreditCard color="#2d3436" />;
                                  }
                                })()}
                              </span>
                              <label className="shipping__radio--label ms-2" htmlFor={`payment-${method.id}`}> 
                                <span className="shipping__radio--label__primary">{method.name}</span>
                                <span className="shipping__radio--label__desc">Pay with {method.name}</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="checkout__content--step__inner3 border-radius-5 p-4">
                          <p className="text-danger">No payment methods available. Please contact support.</p>
                        </div>
                      )}
                    </div>
                    <div className="checkout__content--step__footer d-flex align-items-center gap-3">
                      <button 
                        className="continue__shipping--btn btn border-radius-5 px-4 py-2" 
                        type="button"
                        onClick={handleProceedToPay}
                        disabled={!selectedPaymentId || processing}
                      >
                        {processing ? 'Processing...' : 'Place Order'}
                      </button>
                      <Link className="previous__link--content" to="/cart">Return to cart</Link>
                    </div>
                  </form>
                </main>
                <footer className="main__footer main__footer--wrapper mt-4">
                  {/* Footer removed as requested */}
                </footer>
              </div>
              {/* Sticky Sidebar */}
              <aside className="checkout__sidebar sidebar" style={{position: 'sticky', top: 32, alignSelf: 'flex-start', minWidth: 320, maxWidth: 400, background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.07)', padding: 24}}>
                <div className="cart__table checkout__product--table mb-3">
                  <table className="cart__table--inner">
                    <tbody className="cart__table--body">
                      {cart.cart.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center py-4">Your cart is empty</td>
                        </tr>
                      ) : (
                        cart.cart.map((item) => (
                          <tr key={item.id} className="cart__table--body__items">
                            <td className="cart__table--body__list">
                              <div className="cart__product d-flex align-items-center gap-2">
                                <div className="product__thumbnail border-radius-5">
                                  <Link to={`/product/${item.id}`}>
                                    <img className="border-radius-5" src={getItemImage(item)} alt={item.name} style={{width: 48, height: 48, objectFit: 'cover'}} />
                                  </Link>
                                  <span className="product__thumbnail--quantity">{item.quantiy}</span>
                                </div>
                                <div className="product__description">
                                  <h3 className="product__description--name h5 mb-0">
                                    <Link to={`/product/${item.id}`}>{item.name}</Link>
                                  </h3>
                                  {item.store_name && item.store_name !== 'Default Store' && (
                                    <span className="product__description--variant">STORE: {item.store_name}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="cart__table--body__list">
                              <span className="cart__price">${getItemTotal(item)}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="checkout__discount--code mb-3">
                  <form className="d-flex" action="#">
                    <label className="flex-grow-1">
                      <input
                        className="checkout__discount--code__input--field border-radius-5"
                        placeholder="Gift card or discount code"
                        type="text"
                        name="discountCode"
                        value={formData.discountCode}
                        onChange={handleChange}
                      />
                    </label>
                    <button className="checkout__discount--code__btn btn border-radius-5 ms-2" type="submit">Apply</button>
                  </form>
                </div>
                <div className="checkout__total">
                  <table className="checkout__total--table">
                    <tbody className="checkout__total--body">
                      <tr className="checkout__total--items">
                        <td className="checkout__total--title text-left">Subtotal</td>
                        <td className="checkout__total--amount text-right">${subtotal.toFixed(2)}</td>
                      </tr>
                      {discount > 0 && (
                        <tr className="checkout__total--items">
                          <td className="checkout__total--title text-left">Discount</td>
                          <td className="checkout__total--amount text-right">-${discount.toFixed(2)}</td>
                        </tr>
                      )}
                      {deliveryCharge > 0 && (
                        <tr className="checkout__total--items">
                          <td className="checkout__total--title text-left">Delivery</td>
                          <td className="checkout__total--amount text-right">${deliveryCharge.toFixed(2)}</td>
                        </tr>
                      )}
                      <tr className="checkout__total--items">
                        <td className="checkout__total--title text-left">Tax</td>
                        <td className="checkout__total--amount text-right">${tax.toFixed(2)}</td>
                      </tr>
                    </tbody>
                    <tfoot className="checkout__total--footer">
                      <tr className="checkout__total--footer__items">
                        <td className="checkout__total--footer__title checkout__total--footer__list text-left">Total</td>
                        <td className="checkout__total--footer__amount checkout__total--footer__list text-right">${total.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CheckoutPage;
